import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';
import { first } from 'rxjs/internal/operators/first';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { SegmentsService } from '../../core/routing/services/asset-library/segments.service';
import { HeroTileService } from '../../core/routing/services/asset-library/hero-tile.service';
import { PromoContentService } from '../../core/routing/services/asset-library/promo-content.service';
import { Segment } from '../asset-library/segments/interfaces/segments.i';
import { HeroTile } from '../asset-library/hero-tile/hero-tile/interfaces/hero-tile.i';
import { PromoContent } from '../asset-library/promo-content/interfaces/promo-content.i';
import { SkuIdInfoService } from '../../core/routing/services/sku-ids/sku-id-info.service';
import { GlobalConstants } from '../../common/constants/global.constant';
import { AppGlobal } from '../../core/state/app-global';
import { User } from '../account/login/_models/user.model';
import { CreateProfileService } from '../../core/routing/services/profile/create-profile.service';

@Component({
  selector: 'iposcen-sku-ids',
  templateUrl: './sku-ids.component.html',
  styleUrls: ['./sku-ids.component.scss']
})
export class SkuIdsComponent implements OnInit, OnDestroy {
  sideNavItem = 'skuIdInfo';
  segmentList = [];
  customMainTileList = [];
  promoContentList = [];
  allSkuCsvData = [];
  retailerConfig: any;
  isRepairabilityIndexEnabled: boolean;
  isCustomTileEnabled: boolean;
  isPriceSupress: boolean;
  skuIdLength: number;
  userRole: string;
  isSrtAdmin = false;
  downloadFile = new Date().toLocaleDateString() + '_' + GlobalConstants.SKU_CSV_FILENAME + '.csv';
  userId = sessionStorage.getItem(GlobalConstants.USER_ID);
  retailerId: string = sessionStorage.getItem(GlobalConstants.RETAILER_ID);
  isArabic = localStorage.getItem('preferredLanguage') === 'ar-sa';
  isHebrew = localStorage.getItem('preferredLanguage') === 'he';
  private ngUnsubscribe = new Subject<boolean>();
  constructor(private skuIdService: SkuIdInfoService,
    private createProfileService: CreateProfileService,
    private segmentService: SegmentsService,
    private heroTileService: HeroTileService,
    private promoContentService: PromoContentService,
    private appGlobal: AppGlobal) { }

  ngOnInit(): void {
    this.appGlobal.$userAccount
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (userAccount: User) => {
        this.userRole = userAccount.role;
        this.getSegmentGroupDetails();
        this.getCustomMainContentDetails();
        this.getPromotionalContentDetails();
        this.isSrtAdmin = this.userRole === GlobalConstants.SRT_ADMIN ? true : false;
        if (this.userRole === GlobalConstants.RETAIL_HQ_USER || this.userRole === GlobalConstants.STORE_USER || this.userRole === GlobalConstants.REGIONALHQ) {
          this.retailerConfig = (userAccount.configurations !== undefined) ? JSON.parse(userAccount.configurations) : await this.getRetailerConfig();
          this.isRepairabilityIndexEnabled = this.retailerConfig?.isRepairabilityIndexEnabled ? this.retailerConfig?.isRepairabilityIndexEnabled : false;
          this.isCustomTileEnabled = this.retailerConfig?.customSrtEnabled ? this.retailerConfig?.customSrtEnabled : false;
          this.isPriceSupress = this.retailerConfig?.isPriceSupress ? this.retailerConfig?.isPriceSupress : false;
        } else {
          this.isRepairabilityIndexEnabled = false;
          this.isCustomTileEnabled = false;
          this.isPriceSupress = false;
        }
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  getRetailerConfig() {
    return new Promise((resolve) => {
      this.createProfileService.getUserRoleData(this.userId, this.retailerId)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(data => {
          const config = JSON.parse(data?.retailer?.configurations);
          resolve(config);
        })
    })
  }

  getSegmentGroupDetails() {
    if (this.userRole !== GlobalConstants.SRT_ADMIN) {
      this.segmentService.getAllSegmentsByUser()
        .pipe(takeUntil(this.ngUnsubscribe))
        .pipe(first())
        .subscribe(getAllSegmentContentResponse => {
          const resData = (getAllSegmentContentResponse.segment as Segment[]);
          this.segmentList = this.getContentGroupData(resData);
          this.skuIdService.setSegmentContentGroup(this.segmentList);
        }, error => {
          this.skuIdService.setSegmentContentGroup([]);
        });
    }
  }

  getCustomMainContentDetails() {
    if (this.userRole !== GlobalConstants.SRT_ADMIN) {
      this.heroTileService.getAllCustomHeroTileByUser()
        .pipe(takeUntil(this.ngUnsubscribe))
        .pipe(first())
        .subscribe(getAllHeroContentResponse => {
          const resData = (getAllHeroContentResponse.customHeroTile as HeroTile[]);
          this.customMainTileList = this.getContentGroupData(resData);
          this.skuIdService.setMainTileContentGroup(this.customMainTileList);
        }, error => {
          this.skuIdService.setMainTileContentGroup([]);
        });
    }
  }

  getPromotionalContentDetails() {
    if (this.userRole !== GlobalConstants.SRT_ADMIN) {
      this.promoContentService.getAllPromoContentByUser()
        .pipe(takeUntil(this.ngUnsubscribe))
        .pipe(first())
        .subscribe(getAllPromoContentResponse => {
          const resData = (getAllPromoContentResponse.promotionalContent as PromoContent[]);
          this.promoContentList = this.getContentGroupData(resData);
          this.skuIdService.setPromoContentGroup(this.promoContentList);
        }, error => {
          this.skuIdService.setPromoContentGroup([]);
        });
    }
  }

  getContentGroupData(data) {
    let tempArray = [];
    data.forEach(element => {
      try {
        const configObj = (typeof element?.configurations === 'object') ? element?.configurations : JSON.parse(element.configurations);
        element.configurations = configObj;
      }
      catch (err) {
        console.log(err);
      }
      tempArray.push(element);
    });
    if (tempArray) {
      const finalArray = [];
      tempArray.forEach(element => {
        if (element.updateTime) element.updateTime = new Date(element.updateTime);
        finalArray.push({ id: element.id, name: element.configurations.publishedName, updatedTime: element.updateTime });
      });
      return finalArray;
    }
  }

  navigate(value: string) {
    this.sideNavItem = value;
  }
  // For IIT2-6942 story
  downloadCsvFile() {
    if (navigator.onLine) {
      const skuRequestBody = {
        csvHeaders: []
      };
      if (this.userRole !== GlobalConstants.SRT_ADMIN) {
        skuRequestBody.csvHeaders.push(GlobalConstants.SKU_CODE);
        skuRequestBody.csvHeaders.push(GlobalConstants.BATTERY_LIFE);
        if (!this.isPriceSupress) {
          skuRequestBody.csvHeaders.push(GlobalConstants.PRIMARY_PRICE);
          skuRequestBody.csvHeaders.push(GlobalConstants.SECONDARY_PRICE);
          skuRequestBody.csvHeaders.push(GlobalConstants.PRICE_STRIKETHROUGH);
        } 
        if (this.isCustomTileEnabled) {
          skuRequestBody.csvHeaders.push(GlobalConstants.CUSTOM_MAIN_TILE_ID);
        }
        skuRequestBody.csvHeaders.push(GlobalConstants.SEGMENT_ID_CSV);
        skuRequestBody.csvHeaders.push(GlobalConstants.PROMOCONTENT_1);
        skuRequestBody.csvHeaders.push(GlobalConstants.PROMOCONTENT_2);
        skuRequestBody.csvHeaders.push(GlobalConstants.PROMOCONTENT_3);
        if (this.isRepairabilityIndexEnabled) {
          skuRequestBody.csvHeaders.push(GlobalConstants.REPAIRABILITY_INDEX_COL_NAME);
          skuRequestBody.csvHeaders.push(GlobalConstants.DAS_TETE_COL_NAME);
          skuRequestBody.csvHeaders.push(GlobalConstants.DAS_TRONC_COL_NAME);
          skuRequestBody.csvHeaders.push(GlobalConstants.DAS_MEMBRE_COL_NAME);
        }
        skuRequestBody.csvHeaders.push(GlobalConstants.INTERNAL_NAME);
        skuRequestBody.csvHeaders.push(GlobalConstants.NAME_OF_THE_DEVICE);
        skuRequestBody.csvHeaders.push(GlobalConstants.PROCESSOR);
        skuRequestBody.csvHeaders.push(GlobalConstants.GRAPHICS_CSV);
        skuRequestBody.csvHeaders.push(GlobalConstants.STORAGE);
        skuRequestBody.csvHeaders.push(GlobalConstants.MEMORY_CSV);
        skuRequestBody.csvHeaders.push(GlobalConstants.OPERATING_SYSTEM_CSV);
      } else if (this.userRole === GlobalConstants.SRT_ADMIN) {
        skuRequestBody.csvHeaders.push(GlobalConstants.ID);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_NAME);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_PRIORITY);
        skuRequestBody.csvHeaders.push(GlobalConstants.BRAND);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_FORM_FACTOR);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_LINK);
        skuRequestBody.csvHeaders.push(GlobalConstants.FILE_TYPE_IMAGE);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_RATINGS);
        skuRequestBody.csvHeaders.push(GlobalConstants.PRICE);
        skuRequestBody.csvHeaders.push(GlobalConstants.PROCESSOR_COLUMN);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_PROCESSOR_GEN);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_SSD);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_RAM);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_RAM_TYPE);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_DISPLAY);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_RESOLUTION);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_TOUCHSCREEN);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_WEIGHT);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_PORTS_THUNDERBOLT_4);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_PORTS_USBC);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_PORTS_USBA);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_PORTS_HEADPHONE);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_PORTS_HEADPHONE_MIC);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_PORTS_HDMI);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_PORTS_MICRO_SD);
        skuRequestBody.csvHeaders.push(GlobalConstants.SRT_PORTS_SURFACE);
      }
      this.skuIdService.skuPostDownloadCsv(skuRequestBody, this.isRepairabilityIndexEnabled, this.isPriceSupress, this.isCustomTileEnabled, this.retailerId)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(getAllSkuData => {
          var skuResponse: any = getAllSkuData
          this.downloadSkuCsv(skuResponse);
        });
    }
  }

  downloadSkuCsv(csvResponse) {
    this.allSkuCsvData = csvResponse;
    let todayTime = new Date();
    let month = (todayTime.getMonth() + 1);
    let day = ('0' + todayTime.getDate()).slice(-2);
    let year = todayTime.getFullYear();
    let filename = GlobalConstants.SKU_FILENAME;
    let userfilename = month + '_' + day + '_' + year + '_' + filename;
    const blob = new Blob(['\ufeff' + this.allSkuCsvData], { type: 'text/csv;charset=utf-8;' });
    const dwldLink = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const isSafariBrowser = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
    if (isSafariBrowser) {  // if Safari open in new window to save file with random filename.
      dwldLink.setAttribute("target", "_blank");
    }
    dwldLink.setAttribute("href", url);
    dwldLink.setAttribute("download", userfilename + ".csv");
    dwldLink.style.visibility = "hidden";
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }
}
